import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { AxiosError } from 'axios';

import { TokenPrefixEnum } from '@app/core/auth/auth.model';
import { TokenService } from '@app/auth/token.service';
import { IComponentData } from '@app/open-insurance/open-insurance.model';
import { OpenInsuranceService } from '@app/open-insurance/open-insurance.service';
import {
  CONSENTS_TRANSMITTER_ROUTE,
  OPEN_INSURANCE_ROUTE,
  OPEN_INSURANCE_ROUTES
} from '@app/open-insurance/shared/constants/routes';

import { ERROR_COMMAND_MESSAGE } from '../opin-consent/opin-consent.enum';
import {
  ConsentDTO,
  ICompleteConsentPayloadDTO,
  IErrorConsent
} from '../opin-consent/opin-consent.model';
import { OpinConsentService } from '../opin-consent/opin-consent.service';

@Injectable()
export class TransmitterService implements OnDestroy {
  completeSharingFallbackURL: string;

  endFallbackURL: string;
  cancel: () => Promise<void>;

  private destroy$ = new Subject<void>();

  private _componentData: IComponentData[];
  private _storagedConsent: ConsentDTO;

  constructor(
    private readonly router: Router,
    private tokenService: TokenService,
    private readonly opinConsentService: OpinConsentService,
    private readonly openInsuranceService: OpenInsuranceService
  ) {}

  get storagedComponentData(): IComponentData[] | undefined {
    return this._componentData;
  }

  set storagedComponentData(value: IComponentData[]) {
    this._componentData = value;
  }

  get storagedConsent(): ConsentDTO | undefined {
    return this._storagedConsent;
  }

  set storagedConsent(value: ConsentDTO) {
    this._storagedConsent = value;
  }

  private handleErrorCommand(error: unknown) {
    if (error instanceof AxiosError) {
      const {
        response: { data }
      } = error;

      const errorMessage =
        ERROR_COMMAND_MESSAGE[data.type] ?? ERROR_COMMAND_MESSAGE.DEFAULT;

      this.cancelConsentByError({
        fallbackURL: data.redirect.redirectTo,
        institution: {
          name: data.tpp.name,
          thumbnail: data.tpp.logoUrl
        },
        message: errorMessage
      });
    } else {
      throw error;
    }
  }

  async getConsent(): Promise<ConsentDTO> {
    try {
      const authenticatedConsent =
        await this.opinConsentService.authenticateConsent(
          this.openInsuranceService.commandID,
          this.tokenService.getToken(TokenPrefixEnum.Bearer)
        );

      return authenticatedConsent;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const { tpp, message, redirect } = error.response.data as IErrorConsent;

        this.cancelConsentByError({
          fallbackURL: redirect.redirectTo,
          institution: {
            name: tpp.name,
            thumbnail: tpp.logoUrl
          },
          message:
            ERROR_COMMAND_MESSAGE[message] ?? ERROR_COMMAND_MESSAGE.DEFAULT
        });

        return;
      }

      throw error;
    }
  }

  cancelConsentByError(data: {
    fallbackURL: string;
    institution: {
      name: string;
      thumbnail: string;
    };
    message: string;
  }) {
    this.endFallbackURL = data.fallbackURL;

    this.router.navigateByUrl(
      `${OPEN_INSURANCE_ROUTE}/${OPEN_INSURANCE_ROUTES[CONSENTS_TRANSMITTER_ROUTE].FINALIZATION}`,
      {
        state: {
          refused: true,
          title: 'Algo errado aconteceu!',
          message: data.message,
          brand: {
            name: data.institution.name,
            logo: data.institution.thumbnail
          }
        }
      }
    );
  }

  async confirm(
    data: Omit<ICompleteConsentPayloadDTO, 'commandId'>,
    brand: ConsentDTO['participant']
  ): Promise<void> {
    try {
      const completedConsent = await this.opinConsentService.completeConsent(
        {
          ...data,
          commandId: this.openInsuranceService.commandID
        },
        this.tokenService.getToken(TokenPrefixEnum.Bearer)
      );

      this.completeSharingFallbackURL = completedConsent.redirect.redirectTo;

      this.router.navigateByUrl(
        `${OPEN_INSURANCE_ROUTE}/${OPEN_INSURANCE_ROUTES[CONSENTS_TRANSMITTER_ROUTE].FINALIZATION}`,
        {
          state: {
            refused: false,
            brand
          }
        }
      );
    } catch (error: unknown) {
      this.handleErrorCommand(error);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
